import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { ApplicationState } from "../../reducers";
import { createXDomeTheme } from "../../theme";
import { Snackbar, SnackbarCloseReason, ThemeProvider, Typography } from "@material-ui/core";
import Routes from "./Routes";
import { compose } from "redux";
import { getCurrentUser } from "../../api/user";
import { UserActionType } from "../../reducers/user.reducer";
import { Alert } from "../../components/Alert/Alert";
import { SnackbarState } from "../../domain/domain";
import ExportNodeDialog from "../../components/ExportNodeDialog/ExportNodeDialog";
import { UiActionType } from "../../reducers/ui.reducer";
import AddEditNodeDialog from "../ComponentManagementPage/AddEditNodeDialog";
import RepealNodeDialog from "../ComponentManagementPage/RepealNodeDialog";
import { SNACKBAR_AUTO_HIDE_DURATION_MS } from "../../api/common";
import RemoveNodeDialog from "../ComponentManagementPage/RemoveNodeDialog";
import SessionTimeout from "../Global/SessionTimeout";
import Spinner from "../Spinner";
import { ErrorHandlingIntegration } from "../ErrorHandlingIntegration";
import Logger from "../../utils/logger";

type Props = {
    authenticated: boolean,
    dispatch: (msg: any) => void,
    snackbarState: SnackbarState
}

const AppEntryPoint: React.FC<Props> = ({ authenticated, snackbarState, dispatch }) => {

    const theme = createXDomeTheme()
    const [loading, setLoading] = useState<boolean>(false)

    useEffect(() => {
        const token = localStorage.getItem('ACCESS_TOKEN');
        
        if (!token) {
            setLoading(false);
            return;
        }

        setLoading(true);
        getCurrentUser().then(response => {
            setLoading(false);
            dispatch({ type: UserActionType.USER_LOADED, user: response.data });
            Logger.info(
                'User loaded',
                `User ${response.data.username} loaded successfully`,
                {
                    component: 'AppEntryPoint',
                    action: 'loadCurrentUser',
                },
                {
                    userId: response.data.id,
                    username: response.data.username,
                }
            );
        }).catch(error => {
            Logger.error(
                'Failed to load current user',
                'Unable to retrieve current user information',
                {
                    component: 'AppEntryPoint',
                    action: 'loadCurrentUser',
                },
                error
            );
            localStorage.removeItem('ACCESS_TOKEN');
            dispatch({ type: UserActionType.USER_LOGOUT });
            setLoading(false);
        });
    }, [dispatch]);

    if (loading) {
        return (
            <ThemeProvider theme={theme}>
                <Spinner />
            </ThemeProvider>
        )
    }

    const closeSnackbar = () => {
        dispatch({ type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: false, message: snackbarState.message, severity: snackbarState.severity } });
    }

    return (
        <ErrorHandlingIntegration>
            <ThemeProvider theme={theme}>
                <Snackbar
                    open={snackbarState.open}
                    onClick={() => closeSnackbar()}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    style={{ transform: "translateY(60px) translateX(12px)" }}
                    autoHideDuration={SNACKBAR_AUTO_HIDE_DURATION_MS}
                    onClose={(_, reason: SnackbarCloseReason) => {
                        if (reason !== "clickaway") {
                            closeSnackbar();
                        }
                    }}>
                    <Alert onClose={() => { closeSnackbar(); }} title={""} severity={snackbarState.severity}>
                        <Typography>{snackbarState.message}</Typography>
                    </Alert>
                </Snackbar>
                <ExportNodeDialog />
                <AddEditNodeDialog />
                <RepealNodeDialog />
                <RemoveNodeDialog />
                <Routes authenticated={authenticated} />
                <SessionTimeout authenticated={authenticated} />
            </ThemeProvider>
        </ErrorHandlingIntegration>
    );
};

const mapStateToProps = (state: ApplicationState) => {
    return {
        authenticated: state.user.currentUser !== undefined,
        snackbarState: state.ui.snackbarState
    };
};

const mapDispatchToProps = (dispatch: any) => {
    return { dispatch };
}

export default compose<any>(
    connect(mapStateToProps, mapDispatchToProps)
)(AppEntryPoint);